import { match } from 'ts-pattern';
import { IManagerApiModuleManagerBenchmark } from '~/clientApi/managers.model';
import { SupportedPlatforms } from '~/screens/CreatorAudienceDemographics';
import { formatAbbr, formatPercentage } from '~/utils';

import { MetricCardProps } from './MetricCard';

export interface PlatformMetrics {
  title: MetricCardProps['metricTitle'];
  metricSubtitle?: MetricCardProps['metricSubtitle'];
  value: MetricCardProps['metricValue'];
  transformValue: MetricCardProps['metricTransformValue'];
  isDisabled?: boolean;
}

export type ChannelMetrics = Record<SupportedPlatforms, PlatformMetrics[]>;
export type PlatformMetricsSource = Record<
  SupportedPlatforms,
  string | undefined
>;
export const getPlatformMetrics = (
  benchmarks: IManagerApiModuleManagerBenchmark[],
): ChannelMetrics => {
  return {
    instagram: [
      {
        title: 'Average Reach',
        value: benchmarks[0].benchmarks.igAvgFeedReelsStoryReach,
        transformValue: 'number',
      },
      {
        title: 'Average Views',
        // Fallback to old metric if new is not available (GraphAPI v22 Deprecated Impressions)
        value:
          benchmarks[0].benchmarks.igAvgFeedStoryViews ||
          benchmarks[0].benchmarks.igAvgFeedStoryImpressions,
        transformValue: 'number',
      },
      {
        title: 'Average Engagements',
        value: benchmarks[0].benchmarks.igAvgFeedReelsEngagements,
        transformValue: 'number',
      },
      {
        title: 'Follower Engagement Rate',
        value: benchmarks[0].benchmarks.igAvgFeedReelsFollowerEngRate,
        transformValue: 'percentage',
      },
      {
        title: 'Reach Engagement Rate',
        value: benchmarks[0].benchmarks.igAvgFeedReelsReachEngRate,
        transformValue: 'percentage',
      },
      {
        title: 'View Engagement Rate',
        value: benchmarks[0].benchmarks.igAvgReelsViewsEngRate,
        transformValue: 'percentage',
      },
      {
        title: 'Average Reels Views',

        value: benchmarks[0].benchmarks.igAvgReelsViews,
        transformValue: 'number',
      },
      {
        title: 'Average Story Reach',
        value: benchmarks[0].benchmarks.igAvgStoryReach,
        transformValue: 'number',
      },
      {
        title: 'Average Story Views',
        // Fallback to old metric if new is not available (GraphAPI v22 Deprecated Impressions)
        value:
          benchmarks[0].benchmarks.igAvgStoryViews ||
          benchmarks[0].benchmarks.igAvgStoryImpressions,
        transformValue: 'number',
      },
    ],
    tiktok: [
      {
        title: 'Average Views',
        value: benchmarks[0].benchmarks.ttAvgViews,
        transformValue: 'number',
      },
      {
        title: 'Average Engagements',
        value: benchmarks[0].benchmarks.ttAvgEngagements,
        transformValue: 'number',
      },
      {
        title: 'Average Likes',
        value: benchmarks[0].benchmarks.ttAvgLikes,
        transformValue: 'number',
      },
      {
        title: 'Average Comments ',
        value: benchmarks[0].benchmarks.ttAvgComments,
        transformValue: 'number',
      },
      {
        title: 'Average Shares',
        value: benchmarks[0].benchmarks.ttAvgShares,
        transformValue: 'number',
      },
      {
        title: 'View Engagement Rate',
        value: benchmarks[0].benchmarks.ttAvgViewsEngRate,
        transformValue: 'percentage',
      },
    ],
    youtube: [
      {
        title: 'Average Views',
        value: benchmarks[0].benchmarks.ytAvgViews,
        transformValue: 'number',
      },
      {
        title: 'Average Likes',
        value: benchmarks[0].benchmarks.ytAvgLikes,
        transformValue: 'number',
      },
      {
        title: 'Average Comments',
        value: benchmarks[0].benchmarks.ytAvgComments,
        transformValue: 'number',
      },
      {
        title: 'Average Shares',
        value: benchmarks[0].benchmarks.ytAvgShares,
        transformValue: 'number',
      },
    ],
    snapchat: [
      {
        title: 'Profile Tier',
        value: benchmarks[0].benchmarks.snpProfileTier,
        transformValue: 'string',
      },
      {
        title: 'Story Avg View Time',
        value: benchmarks[0].benchmarks.snpStoryAvgViewTimeMs,
        transformValue: 'number',
        metricSubtitle: 'Last 28 days',
      },
      {
        title: 'Saved Story Avg View Time',
        value: benchmarks[0].benchmarks.snpSavedStoryAvgViewTimeMs,
        transformValue: 'number',
        metricSubtitle: 'Last 28 days',
      },
      {
        title: 'Spotlight Avg View Time',
        value: benchmarks[0].benchmarks.snpSpotlightAvgViewTimeMs,
        transformValue: 'number',
        metricSubtitle: 'Last 28 days',
      },
    ],
  };
};

export const getMetricsSource = (
  benchmarks: IManagerApiModuleManagerBenchmark[],
): PlatformMetricsSource => {
  return {
    instagram: benchmarks[0].benchmarks.igBenchmarksSource,
    tiktok: benchmarks[0].benchmarks.ttBenchmarksSource,
    youtube: benchmarks[0].benchmarks.ytBenchmarksSource,
    snapchat: undefined, // TODO: Add snapchat metrics source when available
  };
};

export const getReadableMetricValue = (
  metricValue: MetricCardProps['metricValue'],
  metricTransformValue: MetricCardProps['metricTransformValue'],
) => {
  return match(metricTransformValue)
    .with('number', () => formatAbbr(Number(metricValue) as number))
    .with('percentage', () => formatPercentage(metricValue as number, 'n/a', 1))
    .otherwise(() => metricValue);
};

export const isZeroOrUndefined = (value: string | number | undefined) => {
  if (value === undefined) return true;

  const parsed = typeof value === 'string' ? parseFloat(value) : value;
  return parsed === 0;
};
